/**
 * @file Init apollo client.
 */
import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import {
  InMemoryCache,
  IntrospectionFragmentMatcher,
} from 'apollo-cache-inmemory';
import { createHttpLink } from 'apollo-link-http';
import { withClientState } from 'apollo-link-state';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';

import introspectionQueryResultData from '../../../back/util/fragmentTypes.json';
import getLogger from '../util/logger';
import getConfig from '../util/config';

const log = getLogger(__filename);

const getApolloClient = ({ onNetworkError, onGraphQLErrors } = {}) => {
  const config = getConfig();

  const httpLink = createHttpLink({
    uri: config.graphql.clientUrl,
    credentials: 'include',
  });

  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData,
  });

  const loggerLink = new ApolloLink((operation, forward) => {
    return forward(operation).map(data => {
      const { operationName, variables } = operation;

      log.debug('GraphQL operation:', operationName, {
        variables,
        data: data ? data.data : null,
      });

      return data;
    });
  });
  const errorLink = onError(
    ({ operation, graphQLErrors, networkError, ...other }) => {
      if (graphQLErrors) {
        graphQLErrors.forEach(({ message, locations, path }) =>
          log.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}, OperationName: ${operation.operationName}`,
          ),
        );

        onGraphQLErrors?.({
          operation,
          networkError,
          graphQLErrors,
          ...other,
        });
      }

      if (networkError) {
        log.error(`[Network error]: ${networkError}`);
        onNetworkError?.({
          operation,
          networkError,
          graphQLErrors,
          ...other,
        });
      }
    },
  );

  const cache = new InMemoryCache({ fragmentMatcher }).restore(
    window.__APOLLO_STATE__,
  );

  const stateLink = withClientState({
    cache,
    resolvers: {},
  });

  const setHeadersLink = setContext((_, prevContext = {}) => {
    const { headers = {} } = prevContext ?? {};

    return {
      ...prevContext,
      headers: {
        ...headers,
        // The following header lets GQL know that the calls are coming from web.
        // Do not tamper with.
        'x-app-name': 'web',
      },
    };
  });

  return new ApolloClient({
    link: ApolloLink.from([
      errorLink,
      loggerLink,
      stateLink,
      setHeadersLink,
      httpLink,
    ]),
    cache,
    resolvers: {},
  });
};

export default getApolloClient;
